/* eslint-disable react/style-prop-object, jsx-a11y/label-has-for */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import Truncate from 'react-truncate';

// Components
import Price from 'components/atoms/Price';
import { Icon } from 'components/atoms/Icon';
import Checkbox from 'components/atoms/Checkbox';
import Button from 'components/atoms/Button';
import ParseHTML from 'components/atoms/ParseHTML';
import ServiceBenefits from 'components/molecules/ServiceBenefits';

// Constants
import { SERVICES_TYPE } from 'constants/services';

import './Service.scss';

// Utils
import debounce from 'debounce';
import { isDiscountValid } from 'utils/promotions';

const ns = 'service';

class Service extends Component {
  constructor(props) {
    super(props);
    this.toggle = this.toggle.bind(this);
    this.handleComment = this.handleComment.bind(this);
    this.openModal = this.openModal.bind(this);
    this.handleTruncate = this.handleTruncate.bind(this);
    this.untruncate = this.untruncate.bind(this);
    this.commentBox = React.createRef();
    this.state = {
      expanded: false,
      showComments: false,
      showAlert: false,
      truncated: false,
      truncatedExpanded: false
    };
  }

  selectService(service, comment) {
    const { onSelect, isSelected, readOnly } = this.props;
    if (readOnly) {
      return;
    }
    const isCommentBoxEmpty =
      this.commentBox.current && this.commentBox.current.value.length <= 0;
    const notes =
      comment || (this.commentBox.current && this.commentBox.current.value);
    if (
      service.commentsRequired &&
      (!notes || isCommentBoxEmpty) &&
      !isSelected
    ) {
      this.setState({
        showAlert: true,
        expanded: true
      });
    } else {
      onSelect(service, notes);
    }
  }

  toggle() {
    const { gtmUserEvent, service } = this.props;
    this.setState({ expanded: !this.state.expanded }, () => {
      if (this.state.expanded) {
        gtmUserEvent(
          'Service Description',
          'Expanded',
          'Service Description Expanded',
          'GTM_CURRENT_PAGE > Service List > Service Description Arrow',
          service.name
        );
      }
    });
  }

  handleTruncate(truncated) {
    if (this.state.truncated !== truncated) {
      this.setState({
        truncated
      });
    }
  }

  untruncate(event) {
    event.preventDefault();
    this.setState({
      truncatedExpanded: !this.state.truncatedExpanded
    });
  }

  handleComment(service, comment) {
    const {
      apptServicesIds,
      updateServiceComment,
      gtmUserEvent,
      removeService,
      addRepairNotes,
      addMaintenanceNotes
    } = this.props;
    let commentAction = 'Added';
    const isSelected = apptServicesIds.find(
      serviceId => serviceId === service.id
    );
    if (service.serviceKind === 'repair') {
      addRepairNotes(comment, service.id);
    } else if (service.serviceKind === 'maintenance') {
      addMaintenanceNotes(comment, service.id);
    }

    if (isSelected) {
      updateServiceComment(service.id, comment);
      commentAction = 'Modified';
    }
    if (
      comment.length < 1 &&
      service.serviceKind === 'repair' &&
      service.commentsRequired
    ) {
      removeService(service.id);
    } else if (!isSelected) {
      this.selectService(service, comment);
    }
    this.setState({ showAlert: false });
    gtmUserEvent(
      'Service Comment',
      commentAction,
      `Service Comment ${commentAction}`,
      `GTM_CURRENT_PAGE > Service: ${service.name}`,
      comment
    );
  }

  openModal(service) {
    const { openModal } = this.props;
    openModal(service.description);
  }

  blurOnEnter(event) {
    const key = event.keyCode || event.which;
    if (key === 13) {
      this.commentBox.current.blur();
    }
  }

  serviceComments() {
    const { service, apptServices, intl, type } = this.props;
    const { showComments, showAlert } = this.state;
    let serviceNotes = '';
    if (apptServices.length > 0) {
      const selectedService = apptServices.find(
        apptService => apptService.id === service.id
      );
      if (selectedService) {
        serviceNotes = selectedService.notes;
      }
    }
    const showingComments =
      showComments || service.commentsRequired || serviceNotes;
    const textareaChange = debounce(
      text => this.handleComment(service, text),
      300
    );
    const textAreaProps = classNames(`${ns}__text-area`, {
      [`${ns}__text-area--warning`]: showAlert
    });
    const serviceName =
      type === SERVICES_TYPE.MENU ? service.description : service.name;
    return showingComments ? (
      <div>
        <textarea
          className={textAreaProps}
          placeholder={intl.formatMessage({
            id: 'c8.service.alert.comments.required'
          })}
          onChange={ev => textareaChange(ev.target.value)}
          onKeyUp={ev => this.blurOnEnter(ev)}
          defaultValue={service.notes || serviceNotes}
          ref={this.commentBox}
          aria-label={serviceName}
        />
      </div>
    ) : (
      <Button
        className={`${ns}__comments-button`}
        buttonTheme={'link'}
        onClick={() => {
          this.setState({ showComments: true });
        }}
      >
        {intl.formatMessage({
          id: 'c8.service.button.add.comments'
        })}
      </Button>
    );
  }

  render() {
    const {
      service,
      isSelected,
      className,
      type,
      isExpandable,
      isOptional,
      includedInPackage,
      techComments,
      promotion,
      intl,
      applyPriceCap
    } = this.props;
    const { expanded, truncated, truncatedExpanded } = this.state;
    const classProps = classNames(ns, className);
    const serviceClassProps = classNames(
      `${ns}__row`,
      'no-gutters',
      'position-relative',
      {
        [`${ns}__row--active`]: expanded
      }
    );
    const descriptionClassProps = classNames({
      [`${ns}__collapse`]: true,
      [`${ns}__collapse--closed`]: !expanded
    });
    const titleClassProps = classNames({
      [`${ns}__col--title`]: truncated,
      [`${ns}__col`]: !truncated
    });
    const iconDirection = `chevron__${expanded ? 'up' : 'down'}`;
    const {
      name,
      price,
      discountedPrice,
      description,
      manufacturerCampaignId,
      benefits,
      priceCaption
    } = service;
    const isCollapsible =
      (isExpandable &&
        (service.description || service.serviceKind === 'repair')) ||
      priceCaption;
    const displayPriceCaption = !!priceCaption;
    const { inputName, inputType, serviceName } =
      type === SERVICES_TYPE.MENU
        ? {
            inputName: 'package',
            inputType: 'radio',
            serviceName: description
          }
        : {
            inputName: 'service',
            inputType: 'checkbox',
            serviceName: name
          };
    const showPromotion = promotion && isDiscountValid(price, discountedPrice);
    const recallCode = ` ${manufacturerCampaignId}`;
    const ariaLabelConfig = intl.formatMessage({
      id: 'c8.aria.expand.title.more',
      defaultMessage: `Expand Service Title`
    });
    return (
      <div className={classProps}>
        <Row
          className={serviceClassProps}
          onKeyUp={event => {
            if (event.key === 'Enter') {
              const elementClass = event.target.className;
              if (elementClass.indexOf('checkbox') !== -1 && isOptional) {
                this.selectService(service, service.notes);
              }
            }
          }}
          aria-label={serviceName}
          role={'listitem'}
        >
          {/* the next two divs act as toggles to the row */}
          <div
            onClick={isCollapsible ? this.toggle : () => {}}
            className={`${ns}__expand-area position-absolute w-20 h-100`}
          />
          <div
            className={`${ns}__select-service-area position-absolute h-100`}
            onClick={
              isOptional
                ? () => this.selectService(service, service.notes)
                : () => {}
            }
          />
          <Col xs={1} className={`${ns}__col ${ns}__icon-container`}>
            {isCollapsible && (
              <Icon type={iconDirection} className={`${ns}__icon`} />
            )}
          </Col>
          <Col xs={7} md={showPromotion ? 8 : 9} className={titleClassProps}>
            <Truncate
              className={`${ns}__name`}
              aria-disabled={true}
              lines={!truncatedExpanded && 2}
              ellipsis={
                <Button
                  buttonTheme={'link'}
                  id={'truncate_title_button'}
                  tabIndex={0}
                  onClick={this.untruncate}
                  aria-label={ariaLabelConfig}
                >
                  {'...'}
                  {intl.formatMessage({
                    id: 'c8.services.page.packages.list.expand.title.more',
                    defaultMessage: 'more'
                  })}
                </Button>
              }
              onTruncate={this.handleTruncate}
            >
              <ParseHTML html={serviceName} />
            </Truncate>
          </Col>
          {isOptional && (
            <Col
              xs={2}
              md={showPromotion ? 2 : 1}
              className={`${ns}__col ${ns}__col--center ${ns}__col--vertical`}
            >
              {showPromotion && (
                <span
                  className={`${ns}__price ${ns}__price--promotion`}
                  aria-disabled={true}
                >
                  <Price type={type} value={price} />
                </span>
              )}
              <span className={`${ns}__price`} aria-disabled={true}>
                <Price
                  type={type}
                  value={showPromotion ? discountedPrice : price}
                  displayCaption={displayPriceCaption || applyPriceCap}
                />
              </span>
            </Col>
          )}
          {type === SERVICES_TYPE.MENU && (
            <Col
              xs={{ size: 7, offset: 1 }}
              md={{ size: 4, offset: 1 }}
              className={`${ns}__package-details`}
            >
              <Button
                tabIndex={0}
                buttonTheme={'link'}
                className={'button--light'}
                onClick={() => this.openModal(service)}
              >
                {intl.formatMessage({
                  id: 'c8.services.page.packages.list.button.details'
                })}
              </Button>
            </Col>
          )}
          {type === SERVICES_TYPE.MENU && (
            <Col
              xs={{ size: 10, offset: 1 }}
              md={{ size: 10, offset: 1 }}
              className={`${ns}__package-benefits`}
            >
              <ServiceBenefits
                benefits={benefits}
                target={`tooltipBenefits-${service.id}`}
                ns={ns}
              />
            </Col>
          )}
          {applyPriceCap && (
            <Col xs={{ size: 10, offset: 1 }} className={`${ns}__capped-price`}>
              {'* '}
              {intl.formatMessage({
                id: 'c8.service.capped.pricing.applied',
                defaultMessage: 'Capped Pricing Applied'
              })}
            </Col>
          )}
          {isOptional && (
            <Col
              xs={2}
              md={1}
              className={`${ns}__col ${ns}__col--center ${ns}__col--checkbox`}
            >
              <Checkbox
                isSelected={isSelected}
                type={inputType}
                name={inputName}
              />
            </Col>
          )}
          {includedInPackage &&
            !isOptional && (
              <Col xs={4} md={2} className={`${ns}__col ${ns}__col--center`}>
                <span className={`${ns}__included-label`} aria-disabled={true}>
                  {intl.formatMessage({
                    id: 'c8.service.label.included.in.package'
                  })}
                </span>
              </Col>
            )}
          {manufacturerCampaignId && (
            <Col className={`recall-code col-10`} aria-disabled={true}>
              {intl.formatMessage({
                id: 'c8.services.page.recall.code',
                defaultMessage: 'Recall Code:'
              })}
              {recallCode}
            </Col>
          )}
        </Row>
        {isCollapsible &&
          expanded && (
            <Row noGutters className={descriptionClassProps}>
              <Col xs="1" />
              <Col xs="10">
                {priceCaption && (
                  <p className={`${ns}__price-caption`}>*{priceCaption}</p>
                )}
                {service.description && (
                  <span className={`${ns}__description`}>
                    <ParseHTML html={service.description} />
                  </span>
                )}
                {techComments && (
                  <span className={`${ns}__description`}>{techComments}</span>
                )}
                {service.serviceKind === 'repair' &&
                  expanded &&
                  this.serviceComments()}
              </Col>
            </Row>
          )}
      </div>
    );
  }
}

Service.propTypes = {
  addMaintenanceNotes: PropTypes.func.isRequired,
  addRepairNotes: PropTypes.func.isRequired,
  apptServices: PropTypes.array,
  apptServicesIds: PropTypes.array,
  className: PropTypes.string,
  gtmUserEvent: PropTypes.func.isRequired,
  includedInPackage: PropTypes.bool,
  intl: PropTypes.object,
  isExpandable: PropTypes.bool,
  isOptional: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  openModal: PropTypes.func,
  promotion: PropTypes.bool,
  removeService: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
  techComments: PropTypes.string,
  type: PropTypes.oneOf([SERVICES_TYPE.ALACARTE, SERVICES_TYPE.MENU]),
  updateServiceComment: PropTypes.func.isRequired,
  applyPriceCap: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool
};

Service.defaultProps = {
  isSelected: false,
  type: SERVICES_TYPE.ALACARTE,
  isExpandable: true,
  isOptional: true,
  promotion: false,
  readOnly: false
};

export default Service;



// WEBPACK FOOTER //
// ./src/components/atoms/Service/Service.js